import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { MdHub, MdLockOutline } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { fetchPartnerAttribution, updatePartnerAttribution } from '../../../services/Http/Api';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';

const RetailerPartnerReferral = () => {
    const { t } = useTranslation();
    const [attribution, setAttribution] = useState(null);
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        let alive = true;
        fetchPartnerAttribution()
            .then((data) => {
                if (!alive) return;
                setAttribution(data);
                setCode(data?.referral_code || '');
            })
            .catch((requestError) => {
                if (alive) setError(requestError.response?.data?.error || t('PartnerReferral.loadError'));
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [t]);

    const locked = Boolean(attribution?.locked_at);
    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError('');
        setMessage('');
        try {
            const updated = await updatePartnerAttribution(code.trim());
            setAttribution(updated);
            setCode(updated?.referral_code || code.trim());
            setMessage(t('PartnerReferral.saved'));
        } catch (requestError) {
            const errors = requestError.response?.data?.errors;
            setError(errors?.referral_code?.[0] || requestError.response?.data?.error || requestError.response?.data?.message || t('PartnerReferral.saveError'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <PageContainer><LoadingMessage /></PageContainer>;

    return (
        <PageContainer>
            <PageHeader><PageTitle icon={<MdHub size={26} />}>{t('PartnerReferral.title')}</PageTitle></PageHeader>
            <PageContent>
                <Panel>
                    <Heading>{t('PartnerReferral.heading')}</Heading>
                    <Copy>{t('PartnerReferral.description')}</Copy>
                    {attribution?.partner_name && <PartnerName>{attribution.partner_name}</PartnerName>}
                    <form onSubmit={submit}>
                        <Label htmlFor="referral-code">{t('PartnerReferral.codeLabel')}</Label>
                        <FormRow>
                            <Input
                                id="referral-code"
                                value={code}
                                onChange={(event) => setCode(event.target.value)}
                                maxLength={64}
                                autoComplete="off"
                                disabled={locked || saving}
                                required
                            />
                            {!locked && <MainButton type="submit" disabled={saving || !code.trim()}>{saving ? t('Common.saving', { defaultValue: 'Saving...' }) : t('PartnerReferral.save')}</MainButton>}
                        </FormRow>
                    </form>
                    {locked && <Notice><MdLockOutline size={19} />{t('PartnerReferral.locked', { date: new Date(attribution.locked_at).toLocaleDateString() })}</Notice>}
                    {error && <Feedback $error>{error}</Feedback>}
                    {message && <Feedback>{message}</Feedback>}
                    <Footnote>{t('PartnerReferral.terms')}</Footnote>
                </Panel>
            </PageContent>
        </PageContainer>
    );
};

const Panel = styled.section`max-width:760px;background:#fff;border:1px solid #e1e7ed;border-radius:8px;padding:1.5rem`;
const Heading = styled.h2`margin:0 0 .55rem;font-size:1.2rem;color:#243b53`;
const Copy = styled.p`margin:0 0 1rem;color:#64748b;line-height:1.55`;
const PartnerName = styled.div`margin:0 0 1rem;font-weight:650;color:#253b50`;
const Label = styled.label`display:block;font-size:.9rem;font-weight:600;margin:0 0 .4rem`;
const FormRow = styled.div`display:flex;align-items:center;gap:.75rem;max-width:590px;@media(max-width:520px){align-items:stretch;flex-direction:column}`;
const Input = styled.input`min-width:0;flex:1;padding:.72rem .8rem;border:1px solid #cbd5df;border-radius:6px;font:inherit; &:disabled{background:#f4f6f8;color:#657487}`;
const Notice = styled.div`display:flex;align-items:flex-start;gap:.5rem;margin-top:1rem;color:#5b6470;font-size:.9rem`;
const Feedback = styled.p`margin:.85rem 0 0;color:${({ $error }) => ($error ? '#b42318' : '#14734a')};font-size:.9rem`;
const Footnote = styled.p`margin:1.2rem 0 0;color:#748292;font-size:.84rem;line-height:1.5`;

export default RetailerPartnerReferral;
